<template>
  <div v-if="isLoading" class="flex flex-col justify-center items-center py-20 space-y-4">
    <h1 class="text-xl font-heading">Confirming order...</h1>
    <div class="w-full max-w-sm bg-gray-200 rounded-full h-2 overflow-hidden">
      <div class="bg-primary h-2 rounded-full w-1/4 animate-indeterminate-progress"></div>
    </div>
  </div>
  <div v-else-if="error.message" class="text-center py-20">
    <h1 class="text-3xl font-bold font-heading mb-4">Oops! Something went wrong.</h1>
    <p class="mb-8" id="error-message">{{ error.message }}</p>
    <router-link
      :to="{ name: 'setShow' }"
      class="text-button-guest-primary underline hover:no-underline"
      id="return-to-shows"
    >
      Return to shows
    </router-link>
  </div>
  <section
    v-else-if="currentShow && performance && reservation"
    class="px-4 max-w-screen-xl mx-auto pb-10 pt-10"
  >
    <h1 class="text-3xl font-bold font-heading mb-8">
      <span v-if="guest" id="guest-name">{{ guest.first_name }}, </span>Your Order Is Confirmed!
    </h1>
    <div class="flex flex-col lg:flex-row lg:space-x-20">
      <div class="w-full lg:w-2/5">
        <div class="flex space-x-4 mb-6">
          <img
            :src="currentShow.image"
            :alt="currentShow.name"
            class="rounded-md w-20 h-20 object-cover border border-linear-strong"
          />
          <div>
            <h2 class="font-heading font-semibold text-lg" id="show-name">
              {{ currentShow.name }}
            </h2>
            <span class="font-sans" id="formattedDateTime">
              <span id="performanceDate" class="font-sans inline">{{ performanceDate }}</span>
              <span id="showtime" class="block">{{ showtime }}</span>
            </span>
          </div>
        </div>

        <div id="numberOfGuests">{{ numberOfGuests }} Guests</div>
        <div id="seatingTimeDisplay" class="mb-4">{{ seating_time?.time }} Seating Time</div>

        <OrderConfirmationDetailTable />

        <div class="font-sans my-8" v-if="guest">
          We received your order and sent an email confirmation to {{ guest.email }}.
        </div>
        <div>Thank you!</div>
        <div>{{ venue_name }}</div>
      </div>

      <div class="w-full lg:w-3/5 mt-12 flex justify-end">
        <RoomMap id="room-map" :backgroundMode="roomMapBgMode">
          <RoomTable
            v-for="table in performance.tables"
            :key="table.id"
            :height="table.h"
            :width="table.w"
            :rotation="table.rotation"
            :shape="table.shape"
            :x="table.x"
            :y="table.y"
            :marker="reservation.tables.includes(table.id) ? 'checkmark' : 'none'"
            :fillClass="
              reservation.tables.includes(table.id) ? 'fill-primary' : 'fill-table-available'
            "
          />
        </RoomMap>
      </div>
    </div>
    <div class="mt-8 text-button-guest-primary">
      <router-link :to="{ name: 'setShow' }"> Return to shows. </router-link>
    </div>
  </section>
</template>

<script lang="ts" setup>
import OrderConfirmationDetailTable from '../components/organisms/OrderConfirmationDetailTable.vue'
import useOrderConfirmationStore from '../stores/OrderConfirmationStore'
import { useCssVar } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref } from 'vue'

import RoomTable from '@/common/components/molecules/RoomTable.vue'
import RoomMap from '@/common/components/organisms/RoomMap.vue'
import { ROOM_MAP_BG_MODE, RoomMapBgMode } from '@/common/interfaces/RoomMap'
import { StoreShow } from '@/models'
import { useSettingStore } from '@/stores/setting'
import { ErrorData, blankError, displayHandled } from '@/utils/errors'

import { GuestRead } from '@generated/types'

const props = defineProps<{
  reservationId: string
  reservationKey: string
}>()

const mode = useCssVar('--mode')
const roomMapBgMode = computed<RoomMapBgMode>(() => {
  return mode.value || (ROOM_MAP_BG_MODE.FORCE_LIGHT as RoomMapBgMode)
})

const settingStore = useSettingStore()
const { venue_currency, venue_name } = storeToRefs(settingStore)

const orderConfirmationStore = useOrderConfirmationStore()
const { performance, reservation, tableModels, pricing, seating_time } =
  storeToRefs(orderConfirmationStore)

const currentShow = computed<StoreShow | undefined>(() => performance.value?.show)
const guest = computed<GuestRead | undefined>(() => reservation.value?.contact)
const numberOfGuests = computed<number>(() => reservation.value?.number_of_guests ?? 0)
const showtime = computed<string>(() => {
  if (!performance.value) return ''
  return `${settingStore.timeStr(performance.value.datetime)} Showtime`
})
const performanceDate = computed<string>(() => {
  if (!performance.value) return ''
  return `${settingStore.fullDate(performance.value.date)}`
})

const isLoading = ref<boolean>(true)
const error = ref<ErrorData>(blankError())
onMounted(async () => {
  try {
    await orderConfirmationStore.loadOrderDetails(props.reservationId, props.reservationKey)
  } catch (e) {
    displayHandled(e, error.value)
  } finally {
    isLoading.value = false
  }

  if (!window.gtag) return
  window.gtag('event', 'purchase', {
    transaction_id: reservation.value?.id,
    value: reservation.value?.amount,
    currency: venue_currency.value,
    reservation_id: reservation.value?.id,
    items: [
      {
        item_id: performance.value?.id,
        item_name: performance.value?.show.name,
        item_variant: tableModels.value.map(t => t.name).join(', '),
        price: pricing.value.total,
        number_of_guests: numberOfGuests.value,
      },
    ],
  })
})
</script>

<style>
@keyframes indeterminate-progress {
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(400%);
  }
}

.animate-indeterminate-progress {
  animation: indeterminate-progress 1.5s linear infinite;
}
</style>
